/**
 * PlayerController - actor that translates user input into pawn movement.
 * Reads keyboard, mouse, and gamepad events, forwarding to the possessed Pawn.
 */
import * as THREE from 'three';


import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import {
  GamepadAxis,
  GamepadButton,
  MouseButton,
  VirtualJoystickIndex
} from '../systems/InputManager.js';
import { Delegate } from '../utils/Delegate.js';
import { property } from '../utils/serialization/decorator.js';
import { ensure } from '../utils/Utils.js';

import { Actor } from './Actor.js';
import { ClientRPC, ServerRPC } from './ActorNetworking.js';
import { PlayerInfo } from './PlayerInfo.js';

import type { ActorOptions } from './Actor.js';
import type { Pawn } from './Pawn.js';
import type {
  GamepadAxisEventDelegate,
  GamepadButtonEventDelegate,
  IInputHandler,
  InputManager,
  KeyboardEventDelegate,
  MouseEventDelegate,
  VirtualJoystickData,
} from '../systems/InputManager.js';
import type { ScenePath } from '../utils/serialization/decorator.js';


/** Options for the PlayerController actor */
export interface PlayerControllerOptions extends ActorOptions {
  /** If true, the player controller will not request pointer lock */
  noPointerLock?: boolean;
}

type InputDevices = 'mouse' | 'gamepad' | 'keyboard' | 'joystick';

@EngineClass('PlayerController')
export class PlayerController extends Actor implements IInputHandler {


  @property({replicate: true, hidden: true, transient: true})
  private pawn: Pawn | null = null;
  private weakOldPawn: WeakRef<Pawn> | null = null;
  @property({replicate: true, hidden: true, transient: true})
  private playerInfo: PlayerInfo | null = null;
  private forward: number = 0;
  private backward: number = 0;
  private left: number = 0;
  private right: number = 0;
  private zoom: number = 0;

  private forwardKeys: string[] = ['w', 'ArrowUp'].map(key => key.toLowerCase());
  private backwardKeys: string[] = ['s', 'ArrowDown'].map(key => key.toLowerCase());
  private leftKeys: string[] = ['a', 'ArrowLeft'].map(key => key.toLowerCase());
  private rightKeys: string[] = ['d', 'ArrowRight'].map(key => key.toLowerCase());
  private zoomInKeys: string[] = ['q'].map(key => key.toLowerCase());
  private zoomOutKeys: string[] = ['z'].map(key => key.toLowerCase());
  private jumpKeys: string[] = [' '].map(key => key.toLowerCase());
  private reloadKeys: string[] = ['r'].map(key => key.toLowerCase());
  private interactKeys: string[] = ['e'].map(key => key.toLowerCase());

  private inputManager: InputManager | null = null;

  @property()
  private noPointerLock: boolean = false;

  /** Discard per-axis pointer-lock deltas above this fraction of the viewport (Chromium spike bug). */
  private static readonly MOUSE_SPIKE_FRACTION = 1 / 3;

  // Movement settings
  public static inputSettings = {
    lookSensitivity: 0.02,
    gamepadLookSensitivity: 80.0,
    joystickLookSensitivity: 0.6,
    joystickMovementSensitivity: 0.03,
  };

  // Add properties to track different input sources
  private mouseLookUp: number = 0;
  private mouseLookRight: number = 0;
  private gamepadLookUp: number = 0;
  private gamepadLookRight: number = 0;
  private joystickLookUp: number = 0;
  private joystickLookRight: number = 0;
  private lastInputDevice: InputDevices = 'keyboard';

  private joystickStartData: Map<VirtualJoystickIndex, VirtualJoystickData> = new Map();

  /** One-frame edge flags for jump, collected from input events and flushed in tickPrePhysics. */
  private jumpPressedThisFrame: boolean = false;
  private jumpReleasedThisFrame: boolean = false;

  public readonly onKeyDown: KeyboardEventDelegate = new Delegate<[KeyboardEvent], boolean>();
  public readonly onKeyUp: KeyboardEventDelegate = new Delegate<[KeyboardEvent], boolean>();
  public readonly onMouseDown: MouseEventDelegate = new Delegate<[MouseButton, MouseEvent], boolean>();
  public readonly onMouseUp: MouseEventDelegate = new Delegate<[MouseButton, MouseEvent], boolean>();

  // Add gamepad delegate events
  public readonly onGamepadButtonDown: GamepadButtonEventDelegate = new Delegate<[number, GamepadButton, number], boolean>();
  public readonly onGamepadButtonUp: GamepadButtonEventDelegate = new Delegate<[number, GamepadButton, number], boolean>();
  public readonly onGamepadAxisChange: GamepadAxisEventDelegate = new Delegate<[number, GamepadAxis, number], boolean>();

  public override initialize(options?: PlayerControllerOptions): void {
    super.initialize(options);

    this.noPointerLock = options?.noPointerLock ?? this.noPointerLock;

    // PlayerControllers should only replicate to their owning client
    this.onlyRelevantToOwner = true;
  }

  public setInputManager(manager: InputManager | null): void {
    this.inputManager = manager;
  }

  public getInputManager(): InputManager | null {
    return this.inputManager;
  }

  public getPawn(): Pawn | null {
    return this.pawn;
  }

  public getPlayerInfo(): PlayerInfo | null {
    return this.playerInfo;
  }

  /**
   * Spawns and initializes a PlayerInfo actor for this controller
   * @param clientId - The client ID for this player
   * @returns - The spawned PlayerInfo actor
   */
  public async spawnPlayerInfo(clientId: string): Promise<PlayerInfo> {
    if (this.playerInfo) {
      console.warn('[PlayerController] PlayerInfo already exists, returning existing instance');
      return this.playerInfo;
    }

    const playerInfoFactory = this.world?.gameMode?.getPlayerInfoFactory() ?? (async () => PlayerInfo.create());
    this.playerInfo = await playerInfoFactory();
    if (!this.playerInfo) {
      throw new Error('Failed to spawn PlayerInfo');
    }
    this.playerInfo.clientId = clientId;
    this.playerInfo.playerName = this.world?.gameMode?.generatePlayerName(clientId) ?? `Player_${clientId.slice(0, 8)}`;

    // Add to world if this controller is already in a world
    if (this.world) {
      this.world.addActors(this.playerInfo);
    }

    return this.playerInfo;
  }

  /**
   * Links an existing PlayerInfo actor to this controller without spawning a new one.
   * Used during map transitions to re-attach a preserved PlayerInfo.
   * @param playerInfo - The PlayerInfo actor to link (must already be in the world)
   */
  public setPlayerInfo(playerInfo: PlayerInfo): void {
    this.playerInfo = playerInfo;
  }

  /**
   * Client RPC: Request client to load a specific map.
   * Called by the server to tell the owning client to load a map.
   * @param scenePath - The path to the scene to load
   */
  @ClientRPC()
  public requestLoadMap(scenePath: ScenePath): void {
    console.log(`[PlayerController] Server requesting map load: ${scenePath}`);

    const gameLoop = this.world?.gameLoop;
    if (!gameLoop) {
      console.warn('[PlayerController] No GameLoop available');
      return;
    }

    gameLoop.openLevel(scenePath).then(() => {
      const world = this.world;
      if (!world) {
        console.warn('[PlayerController] No world available after map load');
        return;
      }
      if (world.isPlaying()) {
        this.notifyMapLoaded();
      } else {
        // world.beginPlay() hasn't fired yet (still initialising subsystems in GameLoop.start()).
        // Scene actors won't have beginPlay/addNetObject called until then, so delay the server
        // notification until the world is fully started — otherwise the server will begin
        // replicating scene actors before they have net IDs assigned.
        const afterWorldBeginPlay = () => {
          world.afterBeginPlay.remove(afterWorldBeginPlay);
          this.notifyMapLoaded();
        };
        world.afterBeginPlay.add(afterWorldBeginPlay);
      }
    }).catch((error: Error) => {
      console.error('[PlayerController] Failed to load map:', error);
    });
  }

  /**
   * Server RPC: Client signals that map loading is complete.
   * Called by the client to tell the server they finished loading the map.
   */
  @ServerRPC()
  public notifyMapLoaded(): void {
    console.log('[PlayerController] Client finished loading map');

    const clientId = this.netOwningClientId;
    if (clientId) {
      this.world?.netWorld?.getMapCoordinator()?.onClientMapLoaded(clientId);
    }
  }

  /**
   * Handles key down events from the input system
   * @param e - The keyboard event
   * @returns True if the event was handled, false otherwise
   */
  public handleKeyDown(e: KeyboardEvent): boolean {
    // Track the active input device
    this.setActiveInputDevice('keyboard');

    if (this.pawn?.handleKeyDown(e)) {
      return true;
    }

    if (this.onKeyDown.invokeWithPredicate(result => result, e).at(-1) ?? false) {
      return true;
    }

    const key = e.key.toLocaleLowerCase();

    if (this.forwardKeys.includes(key)) {
      this.forward = 1;
      return true;
    } else if (this.backwardKeys.includes(key)) {
      this.backward = 1;
      return true;
    } else if (this.leftKeys.includes(key)) {
      this.left = 1;
      return true;
    } else if (this.rightKeys.includes(key)) {
      this.right = 1;
      return true;
    } else if (this.zoomInKeys.includes(key)) {
      this.zoom = 1;
      return true;
    } else if (this.zoomOutKeys.includes(key)) {
      this.zoom = -1;
      return true;
    } else if (this.interactKeys.includes(key)) {
      if (this.pawn?.beginInteract()) {
        return true;
      }
    } else if (this.jumpKeys.includes(key)) {
      if (this.pawn) {
        if (this.pawn.isLocalAutonomousProxy()) {
          // Flag only — component applies jump in its own tickPrePhysics after capturing start state
          this.jumpPressedThisFrame = true;
        } else {
          this.pawn.handleJump();
        }
        return true;
      }
    } else if (this.reloadKeys.includes(key)) {
      if (this.pawn) {
        this.pawn.reload();
        return true;
      }
    }

    return false;
  }


  /**
   * Handles key up events from the input system
   * @param e - The keyboard event
   * @returns True if the event was handled, false otherwise
   */
  public handleKeyUp(e: KeyboardEvent): boolean {
    if (this.pawn?.handleKeyUp(e)) {
      return true;
    }

    if (this.onKeyUp.invokeWithPredicate(result => result, e).at(-1) ?? false) {
      return true;
    }

    const key = e.key.toLocaleLowerCase();

    if (this.forwardKeys.includes(key)) {
      this.forward = 0;
      return true;
    } else if (this.backwardKeys.includes(key)) {
      this.backward = 0;
      return true;
    } else if (this.leftKeys.includes(key)) {
      this.left = 0;
      return true;
    } else if (this.rightKeys.includes(key)) {
      this.right = 0;
      return true;
    } else if (this.zoomInKeys.includes(key)) {
      this.zoom = 0;
      return true;
    } else if (this.zoomOutKeys.includes(key)) {
      this.zoom = 0;
      return true;
    } else if (this.interactKeys.includes(key)) {
      this.pawn?.endInteract();
      return true;
    } else if (this.jumpKeys.includes(key)) {
      if (this.pawn) {
        if (this.pawn.isLocalAutonomousProxy()) {
          // Flag only — component applies stopJump in its own tickPrePhysics
          this.jumpReleasedThisFrame = true;
        } else {
          this.pawn.handleStopJump();
        }
        return true;
      }
    }

    return false;
  }

  /**
   * Handles mouse down events from the input system
   * @param button - The mouse button
   * @param e - The mouse event
   * @returns True if the event was handled, false otherwise
   */
  public handleMouseDown(button: MouseButton, e: MouseEvent): boolean {
    if (this.pawn?.handleMouseDown(button, e)) {
      return true;
    }

    if (this.onMouseDown.invokeWithPredicate(result => result, button, e).at(-1) ?? false) {
      return true;
    }

    if (button === MouseButton.Left) {
      if (this.pawn?.beginFire() ?? false) {
        return true;
      }
    } else if (button === MouseButton.Right) {
      if (this.pawn?.beginAltFire() ?? false) {
        return true;
      }
    }

    return false;
  }

  /**
   * Handles mouse up events from the input system
   * @param button - The mouse button
   * @param e - The mouse event
   * @returns True if the event was handled, false otherwise
   */
  public handleMouseUp(button: MouseButton, e: MouseEvent): boolean {
    if (this.pawn?.handleMouseUp(button, e)) {
      return true;
    }

    if (this.onMouseUp.invokeWithPredicate(result => result, button, e).at(-1) ?? false) {
      return true;
    }

    if (button === MouseButton.Left) {
      if (this.pawn?.endFire() ?? false) {
        return true;
      }
    } else if (button === MouseButton.Right) {
      if (this.pawn?.endAltFire() ?? false) {
        return true;
      }
    }

    return false;
  }

  public handleMouseMove(e: MouseEvent): boolean {
    if (!this.inputManager?.isPointerLocked()) {
      return false;
    }

    this.setActiveInputDevice('mouse');

    const spikeX = window.innerWidth * PlayerController.MOUSE_SPIKE_FRACTION;
    const spikeY = window.innerHeight * PlayerController.MOUSE_SPIKE_FRACTION;
    const dx = Math.abs(e.movementX) > spikeX ? 0 : e.movementX;
    const dy = Math.abs(e.movementY) > spikeY ? 0 : e.movementY;

    this.mouseLookUp += -dy * PlayerController.inputSettings.lookSensitivity;
    this.mouseLookRight += dx * PlayerController.inputSettings.lookSensitivity;
    return true;
  }

  public handleMouseClick(button: MouseButton, e: MouseEvent): boolean {
    return false;
  }

  public handleMouseWheel(e: WheelEvent): boolean {
    // Forward to pawn first, then to movement component
    if (this.pawn?.handleMouseWheel(e)) {
      return true;
    }
    if (this.pawn?.movementComponent?.handleMouseWheel(e)) {
      return true;
    }
    return false;
  }

  /**
   * Handles virtual joystick events from the input system
   * @param index - The index of the virtual joystick
   * @param joystickData - The data from the virtual joystick
   * @returns True if the event was handled, false otherwise
   */
  public handleVirtualJoystick(index: VirtualJoystickIndex, joystickData: VirtualJoystickData): boolean {
    const inputSettings = PlayerController.inputSettings;
    this.setActiveInputDevice('joystick');
    if (joystickData.type === 'start') {
      this.joystickStartData.set(index, joystickData);
    } else if (joystickData.type === 'end') {
      this.joystickStartData.delete(index);
      if (index === VirtualJoystickIndex.Left) {
        this.forward = 0;
        this.backward = 0;
        this.left = 0;
        this.right = 0;
      } else if (index === VirtualJoystickIndex.Right) {
        this.joystickLookRight = 0;
        this.joystickLookUp = 0;
      }
    } else if (joystickData.type === 'move') {
      const startData = this.joystickStartData.get(index);
      if (startData) {
        const deltaX = joystickData.position.x - startData.position.x;
        const deltaY = joystickData.position.y - startData.position.y;
        if (index === VirtualJoystickIndex.Left) {
          this.forward = THREE.MathUtils.clamp(-deltaY * inputSettings.joystickMovementSensitivity, 0, 1);
          this.backward = THREE.MathUtils.clamp(deltaY * inputSettings.joystickMovementSensitivity, 0, 1);
          this.left = THREE.MathUtils.clamp(-deltaX * inputSettings.joystickMovementSensitivity, 0, 1);
          this.right = THREE.MathUtils.clamp(deltaX * inputSettings.joystickMovementSensitivity, 0, 1);
        } else if (index === VirtualJoystickIndex.Right) {
          this.joystickLookRight = deltaX * inputSettings.joystickLookSensitivity;
          this.joystickLookUp = -deltaY * inputSettings.joystickLookSensitivity;
        }
      } else {
        this.joystickStartData.set(index, joystickData);
      }
    }
    return true;
  }

  private setActiveInputDevice(device: InputDevices): void {
    this.lastInputDevice = device;
  }

  /**
   * Handles gamepad button down events from the gamepad system
   * @param gamepadIndex - The index of the gamepad
   * @param buttonIndex - The index of the button
   * @param value - The value of the button press (0.0 to 1.0)
   * @returns True if the event was handled, false otherwise
   */
  public handleGamepadButtonDown(gamepadIndex: number, buttonIndex: number, value: number): boolean {
    // Only handle gamepad 0 by default (first connected gamepad)
    if (gamepadIndex !== 0) return false;

    // Track the active input device
    this.setActiveInputDevice('gamepad');

    if (this.pawn?.handleGamepadButtonDown(gamepadIndex, buttonIndex as GamepadButton, value)) {
      return true;
    }

    if (this.onGamepadButtonDown.invokeWithPredicate(result => result, gamepadIndex, buttonIndex as GamepadButton, value).at(-1) ?? false) {
      return true;
    }

    if (buttonIndex === GamepadButton.FaceBottom) {
      // Jump with bottom face button (A on Xbox, X on PlayStation)
      if (this.pawn) {
        if (this.pawn.isLocalAutonomousProxy()) {
          // Flag only — component applies jump in its own tickPrePhysics after capturing start state
          this.jumpPressedThisFrame = true;
        } else {
          this.pawn.handleJump();
        }
        return true;
      }
    } else if (buttonIndex === GamepadButton.FaceLeft) {
      // Reload with left face button (X on Xbox, Square on PlayStation)
      if (this.pawn) {
        this.pawn.reload();
        return true;
      }
    } else if (buttonIndex === GamepadButton.LeftTrigger) {
      // Begin zoom in
      this.zoom = value;
      return true;
    } else if (buttonIndex === GamepadButton.RightTrigger) {
      // Begin fire
      if (this.pawn?.beginFire() ?? false) {
        return true;
      }
    } else if (buttonIndex === GamepadButton.LeftBumper) {
      // Begin zoom out
      this.zoom = -value;
      return true;
    } else if (buttonIndex === GamepadButton.RightBumper) {
      // Begin alt fire
      if (this.pawn?.beginAltFire() ?? false) {
        return true;
      }
    }

    return false;
  }

  /**
   * Handles gamepad button up events from the gamepad system
   * @param gamepadIndex - The index of the gamepad
   * @param buttonIndex - The index of the button
   * @returns True if the event was handled, false otherwise
   */
  public handleGamepadButtonUp(gamepadIndex: number, buttonIndex: number): boolean {
    // Only handle gamepad 0 by default (first connected gamepad)
    if (gamepadIndex !== 0) return false;

    // Track the active input device
    this.setActiveInputDevice('gamepad');

    if (this.pawn?.handleGamepadButtonUp(gamepadIndex, buttonIndex as GamepadButton)) {
      return true;
    }

    if (this.onGamepadButtonUp.invokeWithPredicate(result => result, gamepadIndex, buttonIndex as GamepadButton, 0).at(-1) ?? false) {
      return true;
    }

    if (buttonIndex === GamepadButton.FaceBottom) {
      // Stop jump with bottom face button (A on Xbox, X on PlayStation)
      if (this.pawn) {
        if (this.pawn.isLocalAutonomousProxy()) {
          // Flag only — component applies stopJump in its own tickPrePhysics
          this.jumpReleasedThisFrame = true;
        } else {
          this.pawn.handleStopJump();
        }
        return true;
      }
    } else if (buttonIndex === GamepadButton.LeftTrigger || buttonIndex === GamepadButton.LeftBumper) {
      // End zoom
      this.zoom = 0;
      return true;
    } else if (buttonIndex === GamepadButton.RightTrigger) {
      // End fire
      if (this.pawn?.endFire() ?? false) {
        return true;
      }
    } else if (buttonIndex === GamepadButton.RightBumper) {
      // End alt fire
      if (this.pawn?.endAltFire() ?? false) {
        return true;
      }
    }

    return false;
  }

  /**
   * Handles gamepad axis change events from the gamepad system
   * @param gamepadIndex - The index of the gamepad
   * @param axisIndex - The index of the axis
   * @param value - The value of the axis (-1.0 to 1.0)
   * @returns True if the event was handled, false otherwise
   */
  public handleGamepadAxisChange(gamepadIndex: number, axisIndex: number, value: number): boolean {
    // Only handle standard gamepad 0
    if (gamepadIndex !== 0) return false;

    // Only track active input device if there's significant input
    // This prevents small axis noise from switching input device
    if (Math.abs(value) > 0.25) {
      this.setActiveInputDevice('gamepad');
    }

    if (this.pawn?.handleGamepadAxisChange(gamepadIndex, axisIndex as GamepadAxis, value)) {
      return true;
    }

    if (this.onGamepadAxisChange.invokeWithPredicate(result => result, gamepadIndex, axisIndex as GamepadAxis, value).at(-1) ?? false) {
      return true;
    }

    if (axisIndex === GamepadAxis.LeftStickX) {
      // Left/Right movement
      if (value > 0) {
        this.right = value;
        this.left = 0;
      } else {
        this.right = 0;
        this.left = -value;
      }
      return true;
    } else if (axisIndex === GamepadAxis.LeftStickY) {
      // Forward/Backward movement
      if (value > 0) {
        this.backward = value;
        this.forward = 0;
      } else {
        this.backward = 0;
        this.forward = -value;
      }
      return true;
    } else if (axisIndex === GamepadAxis.RightStickX) {
      // Look right/left - store in separate gamepad variable
      this.gamepadLookRight = value * PlayerController.inputSettings.gamepadLookSensitivity;
      return true;
    } else if (axisIndex === GamepadAxis.RightStickY) {
      // Look up/down - store in separate gamepad variable
      this.gamepadLookUp = -value * PlayerController.inputSettings.gamepadLookSensitivity;
      return true;
    }

    return false;
  }


  /**
   * Called before physics simulation to process input and update the controlled pawn
   * @param deltaTime - Time elapsed since the last frame in seconds
   */
  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    if (this.pawn) {
      const forwardValue = this.forward - this.backward;
      const rightValue = this.right - this.left;

      // Use look values from the active input device
      let lookUpValue = 0;
      let lookRightValue = 0;

      if (this.lastInputDevice === 'mouse') {
        lookUpValue = this.mouseLookUp;
        lookRightValue = this.mouseLookRight;
      } else if (this.lastInputDevice === 'gamepad') {
        lookUpValue = this.gamepadLookUp;
        lookRightValue = this.gamepadLookRight;
      } else if (this.lastInputDevice === 'joystick') {
        lookUpValue = this.joystickLookUp;
        lookRightValue = this.joystickLookRight;
      }

      const isLookAbsolute = this.lastInputDevice === 'mouse';

      if (this.pawn.isLocalAutonomousProxy()) {
        // Store jump edge flags on the movement component so it can:
        //  1. Record them into the SavedMove in tickPrePhysics (after correction replay)
        //  2. Apply jump/stopJump after capturing the move's start state
        this.pawn.movementComponent?.setJumpEdges(this.jumpPressedThisFrame, this.jumpReleasedThisFrame);

        // Apply movement inputs (look, forward, right, zoom).
        // The component records these into the SavedMove in finalizeLocalMove (tickPostPhysics)
        // and sends the RPC to the server automatically — no manual RPC call needed here.
        this.pawn.addForwardInput(forwardValue, false);
        this.pawn.addRightInput(rightValue, false);
        this.pawn.addLookUpInput(lookUpValue, isLookAbsolute);
        this.pawn.addLookRightInput(lookRightValue, isLookAbsolute);
        this.pawn.addZoomInput(this.zoom, false);
      } else {
        // For non-autonomous proxies (simulated proxies, authority), apply input directly
        // This handles local-only scenarios and server-side application
        // IMPORTANT: On server with multiplayer, don't apply input here - it comes via RPCs
        if (!NetRuntime.isDedicatedServer() || !this.getWorld()?.netWorld?.getGameSession()) {
          this.pawn.addForwardInput(forwardValue, false);
          this.pawn.addRightInput(rightValue, false);
          this.pawn.addLookUpInput(lookUpValue, isLookAbsolute);
          this.pawn.addLookRightInput(lookRightValue, isLookAbsolute);
          this.pawn.addZoomInput(this.zoom, false);
        }
      }
    }

    // Always reset mouse look values each frame
    this.mouseLookUp = 0;
    this.mouseLookRight = 0;

    // Clear one-frame jump edge flags regardless of autonomous/non-autonomous path
    this.jumpPressedThisFrame = false;
    this.jumpReleasedThisFrame = false;

    // For gamepad, apply continuous input or decay
    if (this.lastInputDevice === 'gamepad') {
      // Stick is already at zero or at a maintained value from handleGamepadAxisChange
      // No need for additional decay logic here
    } else {
      // If gamepad is not active, decay its values to avoid lingering input
      const GAMEPAD_DECAY = 0.2;
      if (Math.abs(this.gamepadLookUp) < GAMEPAD_DECAY) {
        this.gamepadLookUp = 0;
      } else if (this.gamepadLookUp > 0) {
        this.gamepadLookUp -= GAMEPAD_DECAY;
      } else {
        this.gamepadLookUp += GAMEPAD_DECAY;
      }

      if (Math.abs(this.gamepadLookRight) < GAMEPAD_DECAY) {
        this.gamepadLookRight = 0;
      } else if (this.gamepadLookRight > 0) {
        this.gamepadLookRight -= GAMEPAD_DECAY;
      } else {
        this.gamepadLookRight += GAMEPAD_DECAY;
      }
    }
  }

  public unpossess(): void {
    if (this.pawn) {
      this.pawn._privateSetPlayerController(null);
    }
    this.pawn = null;
  }

  /**
   * Takes control of the specified pawn
   * @param pawn - The pawn to possess (control)
   */
  public possess(pawn: Pawn): void {
    this.pawn = pawn;
    this.pawn._privateSetPlayerController(this);
  }

  public onRepPawn(): void {
    console.log(`[PlayerController] Pawn changed: ${this.pawn?.uuid}`);

    const oldPawn = this.weakOldPawn?.deref();
    if (oldPawn && oldPawn !== this.pawn && oldPawn?.getPlayerController() === this) {
      oldPawn._privateSetPlayerController(null);
    }
    this.weakOldPawn = this.pawn ? new WeakRef<Pawn>(this.pawn) : null;
  }

  public restartPlayer(): void {
    const playerStart = this.getWorld()?.gameMode?.findPlayerStart(this);
    if (playerStart) {
      ensure(this.pawn);
      const playerStartTransform = playerStart.getWorldTransform();
      this.pawn?.setWorldTransform({
        position: playerStartTransform.position,
        rotation: playerStartTransform.rotation,
      });
    }
  }

  public override getCamera(): THREE.Camera | null {
    const pawnCamera = this.pawn?.getCamera();
    const superCamera = super.getCamera();
    const result = pawnCamera ?? superCamera;
    return result;
  }

  //====================================================================================================

  protected override doBeginPlay(): void {
    super.doBeginPlay();
    console.log(`[PlayerController] doBeginPlay called - localRole: ${this.netLocalRole}, remoteRole: ${this.netRemoteRole}`);
    const world = this.getWorld()!;
    world.consoleManager.addCommandHandler(this);
    // Set up input handling for:
    // 1. Autonomous proxy owned by local client (multiplayer)
    // 2. Authority in non-networked game (single-player)
    const isMultiplayer = !!world.netWorld?.getGameSession();
    const shouldHandleInput = (this.isLocalAutonomousProxy() && this.isOwnedByLocalClient()) ||
      (this.hasAuthority() && !isMultiplayer);

    console.log(`[PlayerController] Input setup - isMultiplayer: ${isMultiplayer}, shouldHandleInput: ${shouldHandleInput}`);

    if (shouldHandleInput) {
      if (!this.noPointerLock) {
        world.inputManager.requestPointerLock({ unadjustedMovement: true });
      }
      world.inputManager.addInputHandler(this);

      // TODO: restartPlayer() should not be called here - GameMode handles spawning/restarting
      // this.restartPlayer();
    }
  }

  public override resetPlayStateForWorldTransition(): void {
    this.getWorld()?.consoleManager.removeCommandHandler(this);
    this.inputManager?.removeInputHandler(this);
    super.resetPlayStateForWorldTransition();
  }

  protected override doEndPlay(): void {
    super.doEndPlay();
    this.getWorld()?.consoleManager.removeCommandHandler(this);
    this.inputManager?.removeInputHandler(this);
  }

  @ConsoleCommand('restartplayer', 'Restart the current player')
  private restartPlayerCommand(): string {
    this.restartPlayer();
    return 'Restarted player';
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Character';
  }
}
